import * as THREE from 'three';
import type { SketchVec3 } from './types';

/**
 * Contour offset — the Offset tool's geometry (walls with thickness).
 *
 * Closed-loop planar offset with miter joins: each edge slides by `distance`
 * along its in-plane normal, adjacent edge copies are intersected to find
 * the new corner. Positive distance offsets toward the loop's right side
 * (inward for CCW contours seen from +normal). Self-intersection cleanup is
 * out of scope for v1 — the guard is the caller's preview showing the loop.
 */

const EPS = 1e-6;

/** Offset an OPEN planar loop (closing implied) by a signed in-plane distance. */
export function offsetLoop(
    loop: SketchVec3[],
    normal: THREE.Vector3,
    distance: number
): SketchVec3[] {
    const n = normal.clone().normalize();
    if (loop.length < 3 || Math.abs(distance) < EPS) return loop.map((p) => [...p] as SketchVec3);

    const pts = loop.map((p) => new THREE.Vector3(...p));
    const m = pts.length;

    // In-plane edge normals: edgeDir × n (points right of travel).
    const edgeNormal = (a: THREE.Vector3, b: THREE.Vector3): THREE.Vector3 =>
        new THREE.Vector3().subVectors(b, a).cross(n).normalize();

    const out: THREE.Vector3[] = [];
    for (let i = 0; i < m; i++) {
        const prev = pts[(i - 1 + m) % m];
        const cur = pts[i];
        const next = pts[(i + 1) % m];
        const nPrev = edgeNormal(prev, cur).multiplyScalar(distance);
        const nNext = edgeNormal(cur, next).multiplyScalar(distance);
        const a = prev.clone().add(nPrev);
        const b = cur.clone().add(nPrev);
        const c = cur.clone().add(nNext);
        const d = next.clone().add(nNext);

        // Miter: intersect the shifted prev-edge (a→b) with the shifted
        // next-edge (c→d) in the face plane.
        const r = b.clone().sub(a);
        const s = d.clone().sub(c);
        const crossRS = new THREE.Vector3().crossVectors(r, s);
        if (crossRS.lengthSq() < EPS * EPS * Math.max(r.lengthSq(), s.lengthSq(), 1)) {
            out.push(b); // near-parallel edges: drop the corner
            continue;
        }
        // Solve a + t·r = c + u·s within the plane.
        const qp = c.clone().sub(a);
        const t = new THREE.Vector3().crossVectors(qp, s).dot(crossRS) / crossRS.lengthSq();
        const clamped = Math.max(-20, Math.min(20, t)); // cap runaway miters
        const corner = a.clone().addScaledVector(r, clamped);
        // Re-project onto the plane through cur (rounding safety).
        corner.addScaledVector(n, -n.dot(corner.clone().sub(cur)));
        out.push(corner);
    }

    return out.map((p) => [p.x, p.y, p.z] as SketchVec3);
}

/** Offset every loop (outer + holes) of a face; holes run opposite. */
export function offsetFaceLoops(
    outer: SketchVec3[],
    holes: SketchVec3[][],
    normal: THREE.Vector3,
    distance: number
): { outer: SketchVec3[]; holes: SketchVec3[][] } {
    return {
        outer: offsetLoop(outer, normal, distance),
        holes: holes.map((h) => offsetLoop(h, normal, -distance)),
    };
}
